(ns minorodata.components.pipeline-editor.details.utils
  (:require
   ["lucide-react" :refer [ChevronDownIcon FilePen PlayIcon Plus RotateCw SaveIcon
                           X]]
   [applied-science.js-interop :as jsi]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.components.spinner.core :as spinner]
   [minorodata.components.transformations.utils :refer [show-transform-settings-modal]]
   [re-posh.core :as re-posh]
   [reitit.frontend.easy :as rt.easy]
   [ui.button :as button]
   [ui.chip :as chip]
   [ui.dropdown-menu :as dropdown-menu]
   [ui.tooltip :as tooltip]))

(defn selected-node-bar [{:keys [selected name description loading sandbox?]}]
  (let [{:keys [preview type]} selected
        description (if loading [spinner/spinner-lucide] description)]
    [:div {:class "h-10 w-full bg-kleene-secondary-01 flex items-center px-10 text-white"}
     [:div {:class "flex-1"}
      [:span {:class "opacity-70"} "Previewing " type  ": "]
      [:span {:class "font-medium inline-flex"}
       name
       (when (and name description) " • ")
       description]]
     [:div {:class "flex gap-2"}
      (when-not sandbox?
        [tooltip/tooltip-root
         [tooltip/tooltip-trigger
          [chip/chip {:variant :error
                      :class "font-bold uppercase bg-kleene-text-error/20"}
           "Live"]]
         [tooltip/tooltip-content
          "You are viewing the live pipeline. Switch to Sandbox mode to make changes."]])
      (when preview
        [:div
         [:a {:href (rt.easy/href :pipeline-editor {} {})
              :class "p-2 hover:opacity-50 transition-opacity"}
          [:> X]]])]]))




(defn preview-command [{:keys [id]} editor]
  (let [model         (jsi/call editor :getModel)
        selection (jsi/call editor :getSelection)
        value (if-let [value (not-empty (jsi/call model :getValueInRange selection))]
                value
                (jsi/call editor :getValue))]
    (if @(re-posh/subscribe [:sandbox.subs/enabled?])
      (dispatch [:sandbox.events/save-and-preview-sandbox-sql {:id id :sql value}])
      (dispatch [:pipeline-editor.api/fetch-transform-preview
                 {:id id :command value}]))))

(def rows-range [200 2000 20000 100000 250000 1000000 10000000])

(defn row-count-dropdown [{:keys [class on-change]}]
  (let [row-count (-> @(re-posh/subscribe [:pipeline-editor.subs/panel])
                      :row-count)
        set-row-count (fn [v]
                        (dispatch-sync [:pipeline-editor.events/set-row-count v])
                        (when (fn? on-change) (on-change v)))]
    [dropdown-menu/dropdown-menu-root
     [dropdown-menu/dropdown-menu-trigger
      [button/button {:variant    :secondary
                      :icon-right ChevronDownIcon
                      :size       :small
                      :class      class}
       (.format (js/Intl.NumberFormat.) row-count) " rows"]]
     [dropdown-menu/dropdown-menu-content
      (for [rows rows-range]
        [dropdown-menu/dropdown-menu-item {:key rows
                                           :on-click #(set-row-count rows)}
         (.format (js/Intl.NumberFormat.) rows) " rows"])]]))

(defn transform-master-id [transform]
  (or (:master-id transform)
      (:sandbox-transform/master-id transform)
      (:transform/master-id transform)
      (:id transform)
      (:sandbox-transform/id transform)
      (:transform/id transform)))

(defn transform-output-tables [transform]
  (or (:outputs transform)
      (:sandbox-transform/outputs transform)
      (:output-tables transform)
      (:transform/output-tables transform)))


(defn preview-sql-button [{:keys [editor-ref transform disabled]}]
  (let [sandbox?        @(re-posh/subscribe [:sandbox.subs/enabled?])
        transform-id    (transform-master-id transform)
        local-transform @(re-posh/subscribe [:pipeline-transform-unsaved/pull [:pipeline-transform-unsaved/id transform-id]])]
    [button/button
     {:size      :small
      :variant   :secondary
      :class     "shrink-0 whitespace-nowrap"
      :disabled  disabled
      :icon      PlayIcon
      :loading   (if sandbox?
                   @(re-posh/subscribe [:mold-http/loading? :sandbox.api/preview-sandbox-sql])
                   @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-transform-preview]))
      :on-click  #(preview-command {:id transform-id}
                                   (jsi/get editor-ref :current))}
     (if (and sandbox? (not-empty local-transform))
       "Save and preview SQL"
       "Preview SQL")]))

(defn tab-actions [{:keys [tab id transform disabled editor-ref]}]
  [:div {:class "flex-1 min-w-0 flex justify-end items-center overflow-x-auto pl-4"}
   (case tab
     "sql"
     [:div {:class "flex shrink-0 items-center gap-2 whitespace-nowrap"}
      (let [sandbox?          @(re-posh/subscribe [:sandbox.subs/enabled?])
            output-tables     (transform-output-tables transform)
            transform-id      (transform-master-id transform)
            has-output-table? (not-empty output-tables)]
        (when sandbox?
          (let [add-unit-test-button
                [button/button
                 {:size      :small
                  :variant   :secondary
                  :class     "shrink-0 whitespace-nowrap"
                  :disabled  (or disabled (not has-output-table?))
                  :icon      Plus
                  :icon-size 16
                  :on-click  (fn []
                               (dispatch [:pipeline-editor.events/show-sandbox-unit-tests
                                          {:transform-id  transform-id
                                           :output-tables output-tables}])
                               (when @(re-posh/subscribe [:pipeline-editor.subs/right-sidebar-collapsed?])
                                 (dispatch [:pipeline-editor.events/toggle-right-sidebar])))}
                 "Add unit test"]]

            (if has-output-table?
              add-unit-test-button
              [tooltip/tooltip-root
               [tooltip/tooltip-trigger
                [:div add-unit-test-button]]
               [tooltip/tooltip-content
                "Unit tests can only be added when this transform has an output table."]]))))

      [preview-sql-button {:editor-ref editor-ref
                           :disabled  disabled
                           :transform transform}]

      [tooltip/tooltip-root (when @(re-posh/subscribe [:sandbox.subs/enabled?])
                              {:open false})
       [tooltip/tooltip-trigger
        [:div
         [button/button
          {:size :small
           :class "shrink-0 whitespace-nowrap"
           :disabled (or disabled
                         (not @(re-posh/subscribe [:sandbox.subs/enabled?])))
           :icon SaveIcon
           :icon-size 16
           :on-click (fn []
                       (dispatch [:sandbox.api/update-transform
                                  {:id (transform-master-id transform)}]))}
          "Save"]]]
       [tooltip/tooltip-content
        "Cannot update SQL code in live mode"]]]

     "details"
     [button/button
      {:size      :small
       :variant   :secondary
       :icon-size 16
       :icon      FilePen
       :on-click  #(show-transform-settings-modal transform)}
      "Edit details"]

     "logs"
     [button/button
      {:size      :small
       :variant   :secondary
       :icon-size 16
       :icon      RotateCw
       :on-click  #(dispatch [:logs.api/fetch])}
      "Refresh logs"]

     "preview-data"
     [:div {:class "flex gap-1"}
      [button/button
       {:size      :small
        :variant   :secondary
        :icon-size 16
        :icon      RotateCw
        :on-click  #(dispatch [:sandbox.api/fetch-table-preview {:params {:id id}}])}
       "Refresh data"]
      [row-count-dropdown]]

     nil)])
